/**
 * Utilities for parsing OpenSCAD imports and dependencies
 */

// Import statement types in OpenSCAD
export type ImportType = 'use' | 'include' | 'import';

export interface ParsedImport {
  type: ImportType;
  path: string;
  filename: string;
  isLibrary: boolean;
  line: number;
}

// Known library prefixes that shouldn't be treated as missing imports
const KNOWN_LIBRARIES = [
  'BOSL2',
  'BOSL',
  'MCAD',
  'NopSCADlib',
  'BOLTS',
  'threads',
  'fonts',
  'LEGO',
  'gridfinity',
];

/**
 * Strip the `#fragment` or `[N]` index suffix from a basename to get the bare
 * filename. Both forms are used for SVG partial imports:
 *   "file.svg#id1"  → "file.svg"
 *   "file.svg[2]"   → "file.svg"
 *
 * The full path (with suffix) is preserved in `ParsedImport.path` so callers
 * that need the selector can access it there.
 */
function stripSelector(basename: string): string {
  // Strip #fragment
  const hashIdx = basename.indexOf('#');
  if (hashIdx !== -1) return basename.slice(0, hashIdx);
  // Strip [N] index
  const bracketIdx = basename.indexOf('[');
  if (bracketIdx !== -1) return basename.slice(0, bracketIdx);
  return basename;
}

/**
 * Parse all import statements from OpenSCAD code
 * Supports: use <file.scad>, include <file.scad>, import("file.stl"),
 *           the fragment form import("file.svg#id"), and the index form
 *           import("file.svg[N]")
 */
export function parseImports(code: string): ParsedImport[] {
  const imports: ParsedImport[] = [];
  const lines = code.split('\n');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip comments
    if (line.startsWith('//') || line.startsWith('/*')) continue;

    // Parse use <path>
    const useMatch = line.match(/^use\s*<([^>]+)>/);
    if (useMatch) {
      const path = useMatch[1];
      imports.push({
        type: 'use',
        path,
        filename: path.split('/').pop() || path,
        isLibrary: isLibraryPath(path),
        line: i + 1,
      });
    }

    // Parse include <path>
    const includeMatch = line.match(/^include\s*<([^>]+)>/);
    if (includeMatch) {
      const path = includeMatch[1];
      imports.push({
        type: 'include',
        path,
        filename: path.split('/').pop() || path,
        isLibrary: isLibraryPath(path),
        line: i + 1,
      });
    }

    // Parse import("path") - for STL, SVG, DXF files.
    // Also handles the fragment form: import("file.svg#id")
    // and the index form: import("file.svg[N]")
    // path preserves the full string (including selector if present);
    // filename strips the selector so it matches the uploaded file's name.
    const importMatches = line.matchAll(/import\s*\(\s*"([^"]+)"/g);
    for (const match of importMatches) {
      const path = match[1];
      const basename = path.split('/').pop() || path;
      imports.push({
        type: 'import',
        path,
        filename: stripSelector(basename),
        isLibrary: false, // imports are never libraries
        line: i + 1,
      });
    }

    // Also check for surface() which imports DAT files
    const surfaceMatches = line.matchAll(
      /surface\s*\(\s*file\s*=\s*"([^"]+)"/g,
    );
    for (const match of surfaceMatches) {
      const path = match[1];
      imports.push({
        type: 'import',
        path,
        filename: path.split('/').pop() || path,
        isLibrary: false,
        line: i + 1,
      });
    }
  }

  return imports;
}

/**
 * Check if a path refers to a known library
 */
function isLibraryPath(path: string): boolean {
  // Check if path starts with a known library
  for (const lib of KNOWN_LIBRARIES) {
    if (path.startsWith(lib + '/') || path === lib) {
      return true;
    }
  }

  // Also check for absolute paths to system libraries
  if (path.startsWith('/')) {
    return true;
  }

  return false;
}

/**
 * Get list of files that are imported but not available
 * @param code The OpenSCAD source code
 * @param availableFiles List of filenames that are available in the project
 * @returns List of missing filenames
 */
export function getMissingImports(
  code: string,
  availableFiles: string[],
): string[] {
  const imports = parseImports(code);
  const missing: string[] = [];

  for (const imp of imports) {
    // Skip library imports
    if (imp.isLibrary) continue;

    // JSON files are resolved by the preprocessor before compilation — they are
    // never passed to the WASM binary as raw includes, so they should not be
    // flagged as "missing" in the file manager UI.
    const ext = imp.filename.split('.').pop()?.toLowerCase();
    if (ext === 'json') continue;

    // Check if the file is available
    if (!availableFiles.includes(imp.filename)) {
      // Don't duplicate
      if (!missing.includes(imp.filename)) {
        missing.push(imp.filename);
      }
    }
  }

  return missing;
}

/**
 * Get unique list of user files (non-library) that need to be imported
 */
export function getUserImports(code: string): ParsedImport[] {
  return parseImports(code).filter((imp) => !imp.isLibrary);
}

/**
 * Generate import statement for a file
 */
export function generateImportStatement(
  filename: string,
  type?: ImportType,
): string {
  const ext = filename.split('.').pop()?.toLowerCase();

  // JS texture scripts use the special texture_script() OpenSCAD module,
  // not any of the standard import mechanisms.
  if (ext === 'js') {
    return `texture_script("${filename}")`;
  }

  // Determine import type based on extension if not specified
  if (!type) {
    if (ext === 'scad') {
      type = 'use';
    } else if (ext === 'json') {
      // JSON files are resolved by the preprocessor via include <>, not import()
      type = 'include';
    } else {
      type = 'import';
    }
  }

  switch (type) {
    case 'use':
      return `use <${filename}>`;
    case 'include':
      return `include <${filename}>`;
    case 'import':
      return `import("${filename}")`;
    default:
      return `import("${filename}")`;
  }
}

/**
 * Get supported file extensions for each import type
 */
export function getSupportedExtensions(): {
  scad: string[];
  import: string[];
  all: string[];
} {
  return {
    scad: ['.scad'],
    import: ['.stl', '.svg', '.dxf', '.dat', '.json'],
    // .js files are texture scripts; .png/.jpg/.webp are texture images —
    // none of these are imported via OpenSCAD import(), but are uploaded via
    // the file manager and referenced by texture_script()/texture_png().
    all: [
      '.scad',
      '.stl',
      '.svg',
      '.dxf',
      '.dat',
      '.json',
      '.txt',
      '.js',
      '.png',
      '.jpg',
      '.jpeg',
      '.webp',
    ],
  };
}

/**
 * Validate if a filename is valid for OpenSCAD
 */
export function isValidFilename(filename: string): boolean {
  // OpenSCAD is fairly permissive, but avoid special characters
  const invalidChars = /[<>:"|?*\x00-\x1f]/;
  if (invalidChars.test(filename)) return false;

  // Must have an extension
  if (!filename.includes('.')) return false;

  // Check for valid extension
  const extensions = getSupportedExtensions().all;
  const ext = '.' + (filename.split('.').pop()?.toLowerCase() || '');
  return extensions.includes(ext);
}

/**
 * Returns true when the filename is a JS texture script.
 */
export function isTextureScript(filename: string): boolean {
  return filename.toLowerCase().endsWith('.js');
}

/**
 * Returns true when the filename is an SVG file that can be used as a
 * texture_svg() source. SVG files can also be used as geometry imports
 * (import("file.svg")) — this helper is used only where texture usage is
 * the explicit intent (e.g. the "Insert as texture_svg()" file manager action).
 */
export function isTextureSvgFile(filename: string): boolean {
  return filename.toLowerCase().endsWith('.svg');
}

/**
 * Generate a texture_svg() statement for the given SVG filename.
 * This is separate from generateImportStatement so that the default behaviour
 * for .svg files (geometry import via import()) is preserved.
 */
export function generateTextureSvgStatement(filename: string): string {
  return `texture_svg("${filename}")`;
}

/**
 * Returns true when the filename is a raster image that can be used as a
 * texture_png() source (PNG, JPG, JPEG, WebP).
 */
export function isTextureImageFile(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return ['png', 'jpg', 'jpeg', 'webp'].includes(ext);
}

/**
 * Generate a texture_png() statement for the given raster image filename.
 */
export function generateTexturePngStatement(filename: string): string {
  return `texture_png("${filename}")`;
}

/**
 * Generate a svg_overlay() statement for the given SVG filename.
 * Produces a ready-to-use snippet with sensible defaults that the user can
 * adjust. The file is placed in the top-left corner at 25% viewer width;
 * height is derived from the SVG's natural aspect ratio.
 */
export function generateSvgOverlayStatement(filename: string): string {
  return (
    `svg_overlay(\n` +
    `  file = "${filename}",\n` +
    `  x = 0.02, y = 0.02,\n` +
    `  width = 0.25\n` +
    `);`
  );
}
